import { dayName, formatHours, halfName } from "./format";
import type { DiaryCell, DiaryView } from "./types";

/**
 * Diary presentation helpers (21 §9–10, 19 §4.3). Pure functions, tested in test/diary.test.ts,
 * plus a small per-browser record of blocks protected for next week (see `NextWeekBlock`).
 */

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export type Half = "am" | "pm";
export type BlockKind = "design" | "email" | "one_to_one";

export const BLOCK_KINDS: { id: BlockKind; label: string; hint: string }[] = [
  { id: "design", label: "Design time", hint: "Working on the firm rather than in it." },
  { id: "email", label: "Email block", hint: "Dealing with the inbox in one sitting." },
  { id: "one_to_one", label: "One-to-one", hint: "Time with one person." },
];

export function blockKindLabel(kind: string): string {
  return BLOCK_KINDS.find((k) => k.id === kind)?.label ?? kind.replace(/_/g, " ");
}

/** Hours a protected block can take: half an hour to four hours (engine validBlock). */
export const HOUR_CHOICES = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4];
export const LABEL_MAX = 60;

export type CellStatus = "past" | "now" | "future" | "holiday";

export function cellStatus(cell: Pick<DiaryCell, "isPast" | "isNow" | "isHoliday">): CellStatus {
  if (cell.isHoliday) return "holiday";
  if (cell.isNow) return "now";
  if (cell.isPast) return "past";
  return "future";
}

export interface GridCell {
  day: Weekday; half: Half; cell: DiaryCell; status: CellStatus;
  /** An empty future half-day can be protected. */
  canProtect: boolean;
  /** A protected future half-day can be released (not once it has started or been broken). */
  canUnprotect: boolean;
}
export interface GridRow { half: Half; label: string; cells: GridCell[] }
export interface DiaryGrid { columns: { day: Weekday; label: string; date: string }[]; rows: GridRow[] }

/** This week as Mon–Fri columns and morning / afternoon rows. */
export function diaryGrid(diary: DiaryView): DiaryGrid {
  const days = WEEKDAYS.map((day) => diary.days.find((d) => d.day === day) ?? null);
  const columns = WEEKDAYS.map((day, i) => ({ day, label: dayName(day).slice(0, 3), date: days[i]?.date ?? "" }));
  const rows = (["am", "pm"] as const).map((half) => ({
    half,
    label: halfName(half),
    cells: WEEKDAYS.flatMap((day, i) => {
      const d = days[i];
      if (!d) return [];
      const cell = d[half];
      const status = cellStatus(cell);
      return [{
        day, half, cell, status,
        canProtect: status === "future" && !cell.protected,
        canUnprotect: status === "future" && !!cell.protected && !cell.protected.raided,
      }];
    }),
  }));
  return { columns, rows };
}

/** A block protected for next week. The diary view only describes this week, so the browser remembers these. */
export interface NextWeekBlock {
  week: number; day: Weekday; half: Half; hours: number; label: string; kind: BlockKind; recurring?: boolean;
  /** A standing one-to-one protected this week, which the engine repeats when next week starts. */
  fromThisWeek?: boolean;
}

export interface NextWeekCell { day: Weekday; half: Half; appointments: string[]; block: NextWeekBlock | null }

/** Next week as the same grid: known appointments, plus blocks this browser saw you protect. */
export function nextWeekGrid(diary: DiaryView, blocks: NextWeekBlock[]): { half: Half; label: string; cells: NextWeekCell[] }[] {
  const week = diary.week + 1;
  return (["am", "pm"] as const).map((half) => ({
    half,
    label: halfName(half),
    cells: WEEKDAYS.map((day) => ({
      day, half,
      appointments: diary.nextWeek.filter((a) => a.day === day && a.half === half).map((a) => a.label),
      block: blocks.find((b) => b.week === week && b.day === day && b.half === half) ?? null,
    })),
  }));
}

export function eveningLine(hours: number): string {
  return hours > 0.05
    ? `${formatHours(hours)} in the evenings and at weekends`
    : "Nothing has spilled into the evenings yet.";
}

/** Planned hours against the office week. Words only; no judgement. */
export function plannedLine(planned: number, office: number): string {
  const over = planned - office;
  if (over > 0.05) return `${formatHours(planned)} planned against ${formatHours(office)} of office hours: ${formatHours(over)} more than the office week holds.`;
  return `${formatHours(planned)} planned against ${formatHours(office)} of office hours.`;
}

/** Protected hours this week, including blocks that were broken. */
export function protectedHours(diary: DiaryView): number {
  return diary.days.reduce((n, d) => n + (d.am.protected?.hours ?? 0) + (d.pm.protected?.hours ?? 0), 0);
}

/** Hours logged so far this week in past and current half-days (decisions, interruptions and protected time). */
export function spentSoFar(diary: DiaryView): number {
  return diary.days.reduce((n, d) => n + d.am.spent + d.pm.spent, 0);
}

export function defaultBlockLabel(kind: BlockKind, personName?: string | null): string {
  if (kind === "one_to_one") return personName ? `One-to-one with ${personName}` : "One-to-one";
  return kind === "email" ? "Email" : "Design time";
}

export interface ProtectForm { label: string; kind: BlockKind; hours: number; personId: string; recurring: boolean }

/** Mirrors the engine's checks so the form can explain itself before sending. */
export function protectFormProblem(f: ProtectForm): string | null {
  const label = f.label.trim();
  if (!label) return "Give the block a short label.";
  if (label.length > LABEL_MAX) return `Keep the label under ${LABEL_MAX} characters.`;
  if (!(f.hours >= 0.5 && f.hours <= 4)) return "A protected block is between half an hour and four hours.";
  if (f.kind === "one_to_one" && !f.personId) return "Choose who the one-to-one is with.";
  return null;
}

/** "Wednesday afternoon" (and "next week" when it is). */
export function slotName(day: string, half: Half, nextWeek = false): string {
  return `${dayName(day)} ${halfName(half).toLowerCase()}${nextWeek ? " next week" : ""}`;
}

/* --- remembered next-week blocks (per browser, per run) ------------------------------------ */

const storeKey = (runId: string) => `lawsim.diary.next.${runId}`;

export function loadNextWeekBlocks(runId: string, currentWeek: number): NextWeekBlock[] {
  try {
    const raw = window.localStorage.getItem(storeKey(runId));
    const list = raw ? (JSON.parse(raw) as NextWeekBlock[]) : [];
    return Array.isArray(list) ? list.filter((b) => b && b.week === currentWeek + 1) : [];
  } catch {
    return [];
  }
}

export function saveNextWeekBlocks(runId: string, blocks: NextWeekBlock[]): void {
  try { window.localStorage.setItem(storeKey(runId), JSON.stringify(blocks)); } catch { /* storage unavailable: forget quietly */ }
}

/** Next week's protected blocks, from the engine's view (plus standing one-to-ones that will repeat). */
export function nextWeekBlocksFromView(diary: DiaryView): NextWeekBlock[] {
  const week = diary.week + 1;
  const out: NextWeekBlock[] = [];
  for (const d of diary.nextWeekDays ?? []) {
    for (const half of ["am", "pm"] as const) {
      const p = d[half].protected;
      if (p) out.push({ week, day: d.day as Weekday, half, hours: p.hours, label: p.label, kind: p.kind as BlockKind, recurring: p.recurring });
    }
  }
  // Recurring blocks are renewed when next week starts; show them as repeating until then.
  for (const d of diary.days) {
    for (const half of ["am", "pm"] as const) {
      const p = d[half].protected;
      if (p?.recurring && !out.some((b) => b.day === d.day && b.half === half)) {
        out.push({ week, day: d.day as Weekday, half, hours: p.hours, label: p.label, kind: p.kind as BlockKind, recurring: true, fromThisWeek: true });
      }
    }
  }
  return out;
}
